<script setup>
import { computed, ref, watch } from "vue";
import { Calendar } from "@element-plus/icons-vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  reference: { type: String, required: true },
  currentCallPeriodId: { type: [Number, String], default: null },
  callPeriodOptions: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
  // F2 (fondo_social/v1-20260923/PLAN_FRONTEND.md): PATCH .../call_period
  // devuelve 409 con este aviso cuando el solicitante ya tiene otra solicitud
  // en la convocatoria elegida — no bloquea, solo advierte, y hay que
  // reenviar con confirmDuplicate:true para forzarlo igualmente.
  duplicateWarning: { type: Object, default: null },
});

const emit = defineEmits(["update:modelValue", "save"]);

const form = ref({ callPeriodId: null });

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const resetForm = () => {
  form.value = { callPeriodId: props.currentCallPeriodId ?? null };
};

watch(
  () => props.modelValue,
  (isVisible) => {
    if (isVisible) resetForm();
  }
);

// Si aparece un aviso de duplicado tras guardar, el diálogo permanece
// abierto (no se resetea el formulario) para que el botón "Confirmar
// igualmente" reenvíe la misma convocatoria seleccionada.
watch(
  () => props.duplicateWarning,
  (warning) => {
    if (warning) form.value.callPeriodId = warning.callPeriodId ?? form.value.callPeriodId;
  }
);

const close = () => {
  if (props.saving) return;
  visible.value = false;
};

const save = () => {
  if (!form.value.callPeriodId) return;
  emit("save", { callPeriodId: form.value.callPeriodId, confirmDuplicate: false });
};

const confirmDuplicate = () => {
  emit("save", { callPeriodId: form.value.callPeriodId, confirmDuplicate: true });
};
</script>

<template>
  <el-dialog v-model="visible" width="480px" :show-close="false" :lock-scroll="true"
    class="social-fund-change-call-period-dialog" @closed="resetForm">
    <section v-if="visible" class="social-fund-change-call-period-dialog__content">
      <img class="social-fund-change-call-period-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
        alt="Cerrar" :class="{ 'social-fund-change-call-period-dialog__close--disabled': saving }" @click="close" />

      <header class="social-fund-change-call-period-dialog__header">
        <el-icon>
          <Calendar />
        </el-icon>
        <div>
          <h2>Cambiar convocatoria</h2>
          <p>Solicitud {{ reference }}</p>
        </div>
      </header>

      <div class="form-item">
        <label for="social-fund-change-call-period">Convocatoria</label>
        <el-select id="social-fund-change-call-period" v-model="form.callPeriodId" class="w-100"
          placeholder="Selecciona una convocatoria" :disabled="saving">
          <el-option v-for="option in callPeriodOptions" :key="option.value" :value="option.value"
            :label="option.label" />
        </el-select>
      </div>

      <div v-if="duplicateWarning" class="social-fund-change-call-period-dialog__warning">
        <p>{{ duplicateWarning.message }}</p>
      </div>

      <footer class="social-fund-change-call-period-dialog__actions">
        <button type="button" class="btn btn_yellow" :disabled="saving" @click="close">CANCELAR</button>
        <button v-if="duplicateWarning" type="button" class="btn btn_red" :disabled="saving" @click="confirmDuplicate">
          {{ saving ? "GUARDANDO..." : "CONFIRMAR IGUALMENTE" }}
        </button>
        <button v-else type="button" class="btn btn_red" :disabled="saving" @click="save">
          {{ saving ? "GUARDANDO..." : "GUARDAR CAMBIOS" }}
        </button>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
:global(.el-dialog.social-fund-change-call-period-dialog) {
  background: #f5f3ff;
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.social-fund-change-call-period-dialog :deep(.el-dialog__header) {
  display: none;
}

.social-fund-change-call-period-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.social-fund-change-call-period-dialog__content {
  color: #263052;
  padding: 8px 16px 14px;
  position: relative;
}

.social-fund-change-call-period-dialog__close {
  height: 38px;
  object-fit: contain;
  position: absolute;
  right: 12px;
  top: 8px;
  width: 38px;
}

.social-fund-change-call-period-dialog__close--disabled {
  cursor: not-allowed;
  opacity: .5;
}

.social-fund-change-call-period-dialog__header {
  align-items: center;
  display: flex;
  gap: 12px;
  padding-right: 48px;
}

.social-fund-change-call-period-dialog__header > .el-icon {
  color: $bg-blue;
  font-size: 31px;
}

.social-fund-change-call-period-dialog__header h2 {
  color: $bg-blue;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -.04em;
  line-height: 1.1;
  margin: 0;
}

.social-fund-change-call-period-dialog__header p {
  color: #737b94;
  font-size: 13px;
  margin: 4px 0 0;
}

.social-fund-change-call-period-dialog .form-item {
  margin-top: 20px;
}

.social-fund-change-call-period-dialog .form-item label {
  color: #283150;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 7px;
}

.social-fund-change-call-period-dialog__warning {
  background: #fff4e5;
  border-radius: 10px;
  color: #8a5a00;
  font-size: 13px;
  margin-top: 16px;
  padding: 12px 14px;
}

.social-fund-change-call-period-dialog__warning p {
  margin: 0;
}

.social-fund-change-call-period-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin: 20px -16px -14px;
  padding: 17px 16px;
}

.social-fund-change-call-period-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  min-width: 180px;
}

.social-fund-change-call-period-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

.social-fund-change-call-period-dialog__actions .btn_red {
  color: #fff !important;
}

@media (max-width: 767px) {
  :global(.el-dialog.social-fund-change-call-period-dialog) {
    max-height: calc(100vh - 24px);
  }

  .social-fund-change-call-period-dialog :deep(.el-dialog__body) {
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }

  .social-fund-change-call-period-dialog__content {
    padding: 18px 16px 14px;
  }

  .social-fund-change-call-period-dialog__close {
    right: 8px;
    top: 7px;
  }

  .social-fund-change-call-period-dialog__header {
    gap: 10px;
    padding-right: 42px;
  }

  .social-fund-change-call-period-dialog__header > .el-icon {
    font-size: 27px;
  }

  .social-fund-change-call-period-dialog__header h2 {
    font-size: 22px;
  }

  .social-fund-change-call-period-dialog__actions {
    flex-direction: column-reverse;
    gap: 10px;
    margin: 20px -16px -14px;
    padding: 14px 16px;
  }

  .social-fund-change-call-period-dialog__actions .btn {
    width: 100%;
  }
}
</style>
